import React, { useState, useMemo, createContext } from 'react';
// Import from 'reactflow' (v11+)
import ReactFlow, {
    ReactFlowProvider,
    MiniMap,
    Controls,
    Background,
    BackgroundVariant,
    ReactFlowInstance, // Replaces OnLoadParams
} from 'reactflow';
import 'reactflow/dist/style.css'; // Import default styles


import { GroupNode } from './components/GroupNode';
import { ConditionNode } from './components/ConditionNode';
import { CountNode } from './components/CountNode';
import { NotNode } from './components/NotNode';
import { EditorPanel } from './components/EditorPanel'; // Import the new EditorPanel

// Import GraphView-specific types
import type { GraphViewProps, GraphViewContextType } from './types/graph';


// Import the new hook
import { usePolicyGraphInteractions } from './hooks/usePolicyGraphInteractions'; // Import the new hook

// Create the context with a default value using the imported GraphViewContextType
export const GraphViewContext = createContext<GraphViewContextType | null>(null);

// === Section: Flow Conversion & Layout Utilities ===
// Using imported utilities from './utils/graphUtils': policyToFlowElements, getLayoutedElements, nodeWidth, nodeHeight.
// Removed inline implementations of policyToFlowElements and getLayoutedElements for modularity.

// === Section: GraphView Component ===
export const GraphView: React.FC<GraphViewProps> = ({ policy, onUpdate }) => {
    const [rfInstance, setRfInstance] = useState<ReactFlowInstance | null>(null);

    const {
        nodes,
        edges,
        onNodesChange,
        onEdgesChange,
        onConnect,
        onNodeClick,
        onPaneClick,
        contextHandlers,
        handleManualLayout,
        editingNodePath,
        conditionToEdit,
        onNodeDragStop,
        handleEditorUpdate
    } = usePolicyGraphInteractions({
        policy,
        onUpdate,
        rfInstance
    });

    const nodeTypes = useMemo(
      () => ({ group: GroupNode, condition: ConditionNode, count: CountNode, not: NotNode }),
      []
    );

    const isRootEmpty = !policy?.properties?.policyRule?.if || Object.keys(policy.properties.policyRule.if).length === 0;

    return (
        <GraphViewContext.Provider value={contextHandlers}>
            <div className="graph-view-container relative w-full border border-gray-300 dark:border-gray-700 rounded bg-gray-50 dark:bg-gray-800" style={{ height: '800px' }}>
                {isRootEmpty && (
                    <div className="absolute inset-0 z-[1000] flex flex-col items-center justify-center bg-gray-100 bg-opacity-80 dark:bg-gray-900 dark:bg-opacity-80 pointer-events-auto">
                        <h3 className="text-lg font-semibold mb-4 text-gray-800 dark:text-gray-100">Create Root Condition</h3>
                        <div className="flex space-x-4">
                            <button onClick={() => contextHandlers.handleInitRoot('condition')} className="px-4 py-2 bg-teal-600 text-white rounded shadow hover:bg-teal-700">Condition</button>
                            <button onClick={() => contextHandlers.handleInitRoot('group')} className="px-4 py-2 bg-blue-600 text-white rounded shadow hover:bg-blue-700">Group</button>
                            <button onClick={() => contextHandlers.handleInitRoot('count')} className="px-4 py-2 bg-purple-600 text-white rounded shadow hover:bg-purple-700">Count</button>
                        </div>
                    </div>
                )}
                <div className="absolute top-4 left-4 z-20 flex">
                    <button
                        onClick={handleManualLayout}
                        className="px-3 py-1 bg-indigo-500 text-white rounded shadow hover:bg-indigo-600 text-sm"
                        title="Re-apply automatic layout"
                    >
                        Re-layout
                    </button>
                </div>
                <ReactFlowProvider>
                    <ReactFlow
                        onInit={setRfInstance}
                        nodes={nodes}
                        edges={edges}
                        onNodesChange={onNodesChange}
                        onEdgesChange={onEdgesChange}
                        onConnect={onConnect}
                        onPaneClick={onPaneClick}
                        onNodeDragStop={onNodeDragStop}
                        nodeTypes={nodeTypes}
                        fitView
                        fitViewOptions={{ padding: 0.1 }}
                        proOptions={{ hideAttribution: true }}
                        style={{ pointerEvents: isRootEmpty ? 'none' : 'auto' }}
                    >
                        <Controls />
                        <Background variant={BackgroundVariant.Dots} gap={12} size={1} />
                    </ReactFlow>
                </ReactFlowProvider>

                {editingNodePath && conditionToEdit && (
                    <EditorPanel
                        editingNodePath={editingNodePath}
                        conditionToEdit={conditionToEdit}
                        handleEditorUpdate={handleEditorUpdate}
                        handleDeleteNode={contextHandlers.handleDeleteNode}
                        handleToggleGroupType={contextHandlers.handleToggleGroupType}
                        handleToggleNot={contextHandlers.handleToggleNot}
                        onClose={() => onPaneClick()}
                    />
                )}

            </div>
        </GraphViewContext.Provider>
    );
}; 

// === GraphView.tsx: Responsibilities ===
// - Policy mutation utilities: Handled by utils/policyUtils.ts
// - Flow conversion utilities: Handled by utils/graphUtils.ts
// - Layout utilities: Handled by utils/graphUtils.ts and internal applyAutoLayout
// - ReactFlow integration: state management, event handlers, and node/edge lifecycle
// - Editor panel UI: Moved to components/EditorPanel.tsx 